import type { PayinsTranslations } from "../interfaces/payins";

export const payinsTranslations: PayinsTranslations = {
  // Countries
  country_BRA: "Brazil",
  country_COL: "Colombia",
  country_MEX: "Mexico",
  country_ARG: "Argentina",

  // Create page
  create_title: "Receive money",
  create_subtitle: "From your clients, quickly and securely",
  button_create: "Create",
  error_create: "Error creating the payin",
  error_no_org: "Your user has no associated organization, the payin cannot be created.",

  // Banner linking to the runtime payments activity view
  runtime_banner_text:
    "Looking for your PIX (Brazil), SPEI (Mexico), CBU/CVU (Argentina) or Bre-B (Colombia) operations? They are processed in real time and live in the new activity view.",
  runtime_banner_link: "View payments activity",

  // Colombia (Bre-B) runtime payin
  breb_form_hint: "A Bre-B QR code will be generated for the payer to transfer from their banking app.",
  breb_success_title: "Bre-B payment created",
  breb_success_id: "ID",
  breb_success_amount: "Amount to pay:",
  breb_success_fee: "Fee",
  breb_success_fee_free: "No fee",
  breb_success_net: "We credit",

  qr_panel_expires_in: "Expires in {{time}}",
  qr_panel_expired: "This QR expired and can no longer be paid. Create a new collection.",
  qr_panel_copy_label: "Copy-and-paste code",
  qr_panel_copy_button: "Copy code",
  qr_panel_copied: "Code copied",
  breb_success_expires: "Expires on",
  breb_success_copied: "Code copied",
  breb_success_new: "Create new payment",
  breb_paid_title: "Payment received",
  breb_paid_text: "The payer completed the transfer and the net amount is already in your wallet.",
  payin_reversed_title: "Payment reversed",
  payin_reversed_text:
    "This payment had been credited and was later reversed: the credited amount was taken back from your wallet. You can create a new collection.",
  breb_rejected_text: "The payment was rejected and nothing was credited. You can create a new charge.",

  // Shared form fields
  form_payer_name_label: "Payer name",
  form_payer_name_placeholder: "First name",
  form_payer_last_name_label: "Payer last name",
  form_payer_last_name_placeholder: "Last name",
  form_card_number_label: "Card number",
  form_card_number_placeholder: "4111 1111 1111 1111",
  form_expiration_date_label: "Expiration date (MM/YY)",
  form_expiration_date_placeholder: "MM/YY",
  form_cvv_label: "CVV",
  form_cvv_placeholder: "123",
  form_cuotes_label: "Installments",
  form_type_label: "Type",
  form_type_amount_sent: "Amount sent",
  form_type_amount_received: "Amount received",
  fee_preview_title: "Breakdown",
  fee_preview_fee: "Fee",
  fee_preview_loading: "Calculating fee…",
  mode_gross_label: "I want to charge",
  mode_net_label: "I want to receive",
  mode_gross_hint: "Type what the payer will transfer; we tell you what you keep.",
  mode_net_hint: "Type what should land in your wallet; we tell you what the payer must transfer.",
  derived_net_text: "You receive {{amount}} {{currency}} in your wallet, after a {{fee}} {{currency}} fee.",
  derived_gross_text:
    "The payer must transfer {{amount}} {{currency}} for you to receive it in full, with a {{fee}} {{currency}} fee.",
  fee_includes_taxes: "Includes {{taxes}}.",
  pix_form_hint: "A Pix QR code will be generated for the payer to transfer from their banking app.",
  spei_form_hint:
    "A CLABE will be generated for this charge only, with an expiry, for the payer to transfer via SPEI from their bank.",
  cbu_cvu_form_hint:
    "Peso transfers arrive at your standing CVU and are credited automatically — this only simulates the fee.",
  pse_form_hint:
    "The payer authorises the debit on their own bank's page. Their details must be registered before the charge is created.",
  nequi_form_hint: "The charge request lands in the payer's Nequi app; they accept it on their phone.",
  bancolombia_button_form_hint:
    "The payer authorises the payment directly at Bancolombia. Their details must be registered before the charge is created.",
  form_payer_rfc_label: "Payer RFC (optional)",
  form_payer_rfc_placeholder: "Leave empty if you don't have it",
  spei_success_title: "SPEI charge created",
  spei_success_clabe_label: "CLABE for this charge",
  spei_success_copy_button: "Copy CLABE",
  spei_success_hint:
    "This CLABE belongs to this charge only and expires. The payer transfers via SPEI from their bank and we credit it as soon as it arrives.",
  spei_paid_title: "Payment credited",
  spei_paid_text: "The transfer arrived and the net amount is already in your wallet.",
  spei_rejected_text: "The payment was rejected and nothing was credited. You can create a new charge.",
  panel_account_title: "Your receiving account",
  panel_account_copy: "Copy",
  panel_account_copied: "Copied",
  panel_simulator_title: "Simulate the fee",
  panel_corridor_unavailable:
    "This corridor is not enabled for your organization yet. Reach out to your account executive.",
  corridor_unavailable_tooltip:
    "Corridor not enabled yet. Reach out to your account executive to turn it on.",
  simulator_gross_hint: "Type what will be transferred to you; we tell you what you keep.",
  simulator_net_hint:
    "Type what should land in your wallet; we tell you how much must be transferred to you.",
  submit_gross: "Create charge for this amount",
  submit_net: "Create charge to receive this amount",
  form_amount_label: "Amount",
  form_amount_usd_label: "Amount (USD)",
  form_payer_document_label: "Payer CPF/CNPJ",
  form_payer_document_placeholder: "Digits only (11 or 14)",
  form_description_label: "Description",
  form_description_placeholder: "Optional note",

  // Colombia form fields
  form_method_label: "Payment method",
  form_method_placeholder: "Select method",
  form_document_type_label: "Document type",
  form_document_type_placeholder: "Select type",
  form_document_label: "Document number",
  form_document_placeholder: "1234567890",
  form_email_label: "Email",
  form_email_placeholder: "user@email.com",
  form_phone_label: "Phone",
  form_phone_placeholder: "+57 300 000 0000",
  form_bank_label: "Bank",
  form_bank_placeholder: "Select bank",
  form_nequi_phone_label: "Nequi number",

  // USA form / file upload
  form_receipt_label: "Receipt",
  upload_instruction: "Click to upload or drag here",
  upload_instruction_drop: "Drop the file here",
  upload_helper: "Upload a voucher of the transaction.",
  upload_constraints: "Max 10 MB • Images (JPG, PNG, WebP)",
  upload_change: "Change",
  upload_remove: "Remove",
  upload_preview_alt: "Voucher preview",

  // Mexico runtime deposit panel (push-detected SPEI — no user-initiated create)

  // Argentina runtime deposit panel (push-detected CBU/CVU — no user-initiated create)
  arg_panel_instructions_title: "Deposit via bank transfer (CBU/CVU)",
  arg_panel_instructions_body:
    "Your organization has a deposit CVU assigned. Make a transfer in Argentine pesos from your bank or wallet — you decide the amount — and the funds will be credited automatically once the deposit is confirmed.",
  arg_panel_cvu_hint: "Don't know your deposit CVU? Request it from your account executive.",
  arg_panel_deposits_title: "Recent deposits",
  arg_panel_deposits_empty: "No deposits registered yet.",
  arg_panel_refresh_aria: "Refresh deposits",
  arg_panel_status_processing: "Processing",
  arg_panel_status_credited: "Credited",
  arg_panel_status_failed: "Rejected",

  // Validation
  validation_payer_name_required: "Payer name is required",
  validation_payer_name_max: "Name cannot exceed 100 characters",
  validation_amount_positive: "Amount must be greater than 0",
  validation_payer_document: "Enter a valid CPF (11 digits) or CNPJ (14 digits)",
  validation_name_required: "Name is required",
  validation_last_name_required: "Last name is required",
  validation_card_number_required: "Card number is required",
  validation_expiration_date_required: "Expiration date is required",
  validation_cvv_required: "CVV is required",
  validation_cuotes_positive: "Installments must be greater than 0",
  validation_card_number_invalid: "Invalid card number",
  validation_expiration_date_invalid: "Invalid expiration date (MM/YY)",
  validation_expiration_date_past: "The card has expired",
  validation_cvv_invalid: "Invalid CVV",
  validation_method_required: "Payment method is required",
  validation_document_type_required: "Document type is required",
  validation_document_required: "Document number is required",
  validation_email_invalid: "Email is not valid",
  validation_phone_required: "Phone is required",
  validation_description_required: "Description is required",
  validation_bank_required: "Bank is required for PSE",
  validation_nequi_phone_required: "Nequi number is required",
  validation_voucher_required: "The receipt is required",
  // Return page (/app/payins/result) — landing after the PSE bank flow
  return_breadcrumb: "Payment result",
  return_missing_title: "Payment not found",
  return_missing_body: "The link carries no valid payment. Check the payments activity.",
  return_waiting_title: "Processing your payment…",
  return_waiting_body:
    "The bank is confirming the transaction. This page refreshes on its own; it can take a few minutes.",
  return_confirmed_title: "Payment confirmed!",
  return_confirmed_body: "The money was credited to the wallet",
  return_rejected_title: "Payment rejected",
  return_rejected_body: "The transaction was rejected or failed at the bank. No balance was credited.",
  return_go_wallets: "Go to wallets",
  return_pse_processing_title: "Processing payment",
  return_pse_pending_callout:
    "Your transaction is PENDING. Please check whether the debit was made at your bank. For inquiries, call {{phone}} or email {{email}}",
  return_pse_autoupdate:
    "This page refreshes automatically. You may also close this window if you wish; the transaction will be confirmed once the payment is confirmed.",
  return_pse_info_cus: "CUS",
  return_pse_info_bank: "Bank",
  return_pse_info_amount: "Amount",
  return_pse_info_detail: "Detail",
  return_pse_info_reference: "Reference",
  return_pse_rejected_body: "The transaction could not be processed. You can try again.",
  return_pse_success_title: "Payment successful",
  return_pse_success_body:
    "Your transaction has been processed. You will receive a confirmation notification within the next 24 hours.",
  return_pse_retry: "Try again",
  return_pse_home: "Back to home",
  pse_contact_phone: "+57 310 8343446",
  pse_contact_email: "operations@colocapayments.com",
  pse_contact_phone_label: "Phone",
  pse_contact_email_label: "Email",

  // Colombia collection flow — shared by /app/payins (Colombia tab) and the COP wallet deposit modal
  step_amount: "Amount",
  step_payer: "Payer",
  step_confirm: "Confirm",
  button_continue: "Continue",
  button_back: "Back",
  submit_pse: "Create PSE payment",
  submit_payer_rail: "Create collection",
  nequi_waiting_title: "The request is already in the payer's app",
  nequi_waiting_body: "We sent the charge to {{phone}}. The payer has to accept it from their Nequi app.",
  nequi_waiting_live: "Waiting for confirmation… you can leave this window open.",
  nequi_waiting_new: "Create another collection",
  rail_confirmed_title: "Payment credited",
  rail_confirmed_body: "The payer accepted the request and the net amount is already in your wallet.",
  rail_confirmed_new: "Create another collection",
  rail_failure_title: "The collection could not be completed",
  rail_failure_generic:
    "The collection did not go through and nothing was credited. You can create a new one.",
  rail_failure_retry: "Create another collection",
  method_choice_label: "How will they pay?",
  method_breb_desc: "They transfer from their banking app by scanning a QR code.",
  method_pse_title: "PSE",
  method_pse_desc: "They authorize the debit on their bank's page. It confirms on its own.",
  method_nequi_desc: "They get the charge request in their Nequi app and accept it on their phone.",
  method_bancolombia_desc: "They authorize the payment directly at Bancolombia. It confirms on its own.",
  method_unavailable: "Not available for your organization yet.",
  method_temporarily_disabled: "Temporarily unavailable. Try again later.",
  amount_limit_hint_range: "Between {{min}} and {{max}} per operation.",
  amount_limit_hint_max: "Up to {{max}} per operation.",
  amount_limit_hint_min: "From {{min}} per operation.",
  amount_limit_hint_by_payer: "Up to {{person}} for individuals and {{company}} for companies.",
  amount_limit_above: "The amount exceeds the maximum allowed of {{max}}.",
  amount_limit_below: "The amount is below the minimum of {{min}}.",
  method_limit_up_to: "Up to {{max}}.",
  method_limit_up_to_by_payer: "Up to {{person}} (individuals) · {{company}} (companies).",
  confirm_limit_above: "For {{payer}} the maximum per operation is {{max}}. Adjust the amount to continue.",
  confirm_limit_below: "For {{payer}} the minimum per operation is {{min}}. Adjust the amount to continue.",
  confirm_limit_edit_amount: "Edit amount",
  limit_payer_person: "individuals",
  limit_payer_company: "companies",
  confirm_amount: "Payer sends",
  confirm_net: "We credit",

  // PSE payer form
  pse_voucher_amount_label: "To pay",
  pse_voucher_reference_label: "Reference",
  pse_voucher_copy: "Copy reference",
  pse_voucher_copied: "Reference copied",
  pse_group_identity: "Who pays",
  pse_group_contact: "Contact and bank",
  pse_person_type_label: "Person type",
  pse_person_natural: "Individual",
  pse_person_juridica: "Company",
  pse_document_type_label: "Type",
  pse_document_number_label: "Document number",
  pse_document_placeholder: "Digits only",
  pse_document_invalid: "Enter a document of 4 to 15 digits",
  pse_name_label: "Full name",
  pse_name_placeholder: "As it appears on the document",
  pse_email_label: "Email",
  pse_email_placeholder: "name@example.com",
  pse_email_invalid: "Enter a valid email",
  pse_phone_label: "Mobile",
  pse_phone_placeholder: "3001234567",
  pse_phone_invalid: "Enter a 10-digit Colombian mobile number starting with 3",
  mobile_phone_invalid: "Enter a 10-digit mobile number starting with 3",
  pse_address_label: "Address",
  pse_address_placeholder: "Calle 100 # 10-20, Bogotá",
  pse_bank_label: "Bank",
  pse_bank_placeholder: "Search for your bank",
  pse_bank_loading: "Loading banks…",
  pse_bank_empty: "No bank matches",
  pse_bank_error: "We could not load the bank list.",
  pse_bank_retry: "Retry",
  pse_bank_hint: "We take you to your bank's page to authorize the debit.",
  pse_max_length: "{{max}} characters maximum",

  // PSE handoff to the bank, and the failure that stops it
  pse_handoff_title: "Creating the transaction",
  pse_handoff_body: "We are setting the payment up with the bank. Do not close this window.",
  pse_handoff_cancel: "Cancel",
  pse_failure_title: "We could not create the payment",
  pse_failure_limit:
    "The transaction amount exceeds the established limits. Please contact our customer service lines.",
  pse_failure_generic:
    "The transaction could not be created. Please try again later or contact our customer service lines.",
  pse_failure_retry: "Try again",
  pse_confirmed_title: "Payment credited",
  pse_confirmed_body: "The payer authorized the debit and the net amount is already in your wallet.",
  pse_confirmed_new: "Create another payment",

  // Own Bre-B key and the QR placard
  qr_card_title: "Pay with {{rail}}",
  qr_card_hint: "Scan it from your banking app",
  qr_card_amount_label: "Amount to pay",
  qr_card_share: "Share",
  qr_card_download: "Download",
  qr_card_key_label: "or transfer to the key",
  breb_key_label: "Your Bre-B key",
  breb_key_copy: "Copy key",
  breb_key_copied: "Key copied",
  breb_key_copy_error: "We could not copy it. Select the key and copy it by hand.",
  breb_key_share: "Share",
  breb_key_share_text: "Send me money over Bre-B to the key {{key}} from your banking app.",
  breb_key_holder_hint: "The bank app will show “{{name}}” as the recipient.",
  breb_key_pending_title: "We are finishing registering your key",
  breb_key_pending_body:
    "Bre-B is confirming it. It usually takes a few seconds; you can close this window and come back later.",
  breb_key_howto_title: "How to top up with your key",
  breb_key_howto_step1: "Copy your key.",
  breb_key_howto_step2: "Open your banking app and choose to transfer with a Bre-B key.",
  breb_key_howto_step3:
    "Paste the key, type the amount and confirm. The money reaches your COP wallet in seconds.",
  breb_key_qr_title: "Your key and your collections with an amount",
  breb_key_qr_body:
    "For a collection with an amount, share that collection's QR: a transfer typed to your key reaches your wallet, but as a separate collection, and the charge stays unpaid.",
  breb_key_qr_fallback:
    "If you delete a key, you will stop receiving on it and on its QR for any amount. If it was your primary key, your next collections with an amount will use the next one, or Coloca's shared key if none is left.",
  breb_key_compact_create: "Create your Bre-B key",
  breb_key_compact_create_hint: "Receive without a QR, from any bank",
  breb_key_entry_cta_view: "See how to top up",
  breb_key_bounds_rule_label: "What happens outside this range",
  breb_key_bounds_rule_range:
    "If someone sends you less than {{min}} or more than {{max}}, the payment will fail. This applies to your key and your QR.",
  breb_key_bounds_rule_min:
    "If someone sends you less than {{min}}, the payment will fail. This applies to your key and your QR.",
  breb_key_bounds_rule_max:
    "If someone sends you more than {{max}}, the payment will fail. This applies to your key and your QR.",
  breb_key_entry_or_amount: "Or create a collection with an amount",
  breb_key_wizard_step_intro: "What it is",
  breb_key_wizard_step_choose: "Your key",
  breb_key_wizard_step_confirm: "Confirm",
  breb_key_intro_title: "Receive money without a QR",
  breb_key_intro_body:
    "Your Bre-B key works as an alias for your COP wallet. Whoever pays you types it in their banking app and the money arrives in seconds. With nothing to scan, it works from the same phone.",
  breb_key_intro_point_banks: "From any bank or wallet in the country, 24/7.",
  breb_key_intro_point_uses: "Use it to top up your wallet or to get paid by your customers.",
  breb_key_intro_point_qr: "You also get a QR for any amount, ready to print or share.",
  breb_key_intro_cta: "Choose my key",
  breb_key_choose_title: "Choose your key",
  breb_key_choose_body: "This is what your payers will type. It always starts with @coloca.",
  breb_key_option_document: "With your document",
  breb_key_option_document_hint:
    "It includes your document number: anyone you share the key with will see it.",
  breb_key_option_phone: "With your mobile",
  breb_key_option_phone_verified_hint:
    "The mobile you registered when verifying your account: anyone you share the key with will see it.",
  breb_key_option_generated: "Assigned key",
  breb_key_option_generated_hint: "We generate a unique key for your account.",
  breb_key_availability_available: "Available",
  breb_key_availability_taken: "Already in use",
  breb_key_availability_unknown: "We could not check it",
  breb_key_availability_checking: "Checking…",
  breb_key_availability_recheck: "Check again",
  breb_key_taken_in_coloca:
    "Another Coloca organization already registered it. If it is yours, contact support.",
  breb_key_taken_in_coloca_notified:
    "Another Coloca organization already registered it. We let them know by email; if it is yours, contact support.",
  breb_key_taken_elsewhere:
    "It is already registered at another Bre-B institution. If it is yours, contact support.",
  breb_key_all_taken_title: "All your key options are already registered",
  breb_key_all_taken_body:
    "Below you can see where each one is registered. If one of them is yours, contact support.",
  breb_key_choose_cta: "Continue",
  breb_key_confirm_title: "Confirm your key",
  breb_key_confirm_permanent: "It stays until you delete it. If you delete it, you can create another one.",
  breb_key_delete: "Delete key",
  breb_key_delete_title: "Delete the key {{key}}?",
  breb_key_delete_body:
    "You will stop receiving transfers to {{key}} and payments through your QR for any amount. Your next collections with an amount will use Coloca's shared key, and you can create a new key afterwards.",
  breb_key_delete_body_final:
    "You will stop receiving transfers to {{key}} and payments through your QR for any amount. Your next collections with an amount will use Coloca's shared key. To get a key of your own again, contact support.",
  breb_key_delete_confirm: "Delete key",
  breb_key_delete_anyway: "Delete anyway",
  breb_key_delete_cancel: "Cancel",
  breb_key_delete_done: "You deleted the key {{key}}.",
  breb_key_delete_two_factor_required: "Turn on two-step verification to delete the key.",
  breb_key_delete_two_factor_description:
    "Enter the code from your authenticator app to delete the key {{key}}.",
  breb_key_confirm_qr: "While it exists, your collection QR codes will use it.",
  breb_key_confirm_cta: "Create my key",
  breb_key_creating: "Registering your key in Bre-B…",
  breb_key_back: "Back",
  breb_key_close: "Close",
  breb_key_ready_title: "Your key is ready!",
  breb_key_ready_body: "Copy or share it and receive from any bank.",
  breb_key_error_title: "We could not create your key",
  breb_key_retry: "Try again",
  breb_key_load_error: "We could not load your Bre-B key.",
  breb_key_verify_title: "Verify your account to create your key",
  breb_key_verify_body:
    "The key receives money from any bank on behalf of your organization, which is why we require a verified account.",
  breb_key_verify_cta: "Verify my account",
  breb_key_rail_disabled: "Bre-B collections are not available for your organization right now.",
  breb_key_not_enabled: "The Bre-B key is not available for your account yet.",
  breb_key_no_options: "We found no data to build your key. Contact support.",
  breb_key_read_only:
    "Your organization does not have a Bre-B key yet. Ask an administrator of your account to create it.",
  breb_key_availability_unknown_body:
    "We could not confirm whether this key is free. Check it again in a few seconds.",
  breb_key_choose_other: "Choose another key",
  breb_key_choice_gone: "That key is no longer available. Go back and choose another one.",
  breb_key_create_amount_charge: "Create a collection with an amount",
  breb_key_bounds_title: "Amounts your key receives",
  breb_key_bounds_min: "Minimum amount",
  breb_key_bounds_max: "Maximum amount",
  breb_key_bounds_hint: "The payer's bank rejects transfers outside this range before sending the money.",
  breb_key_static_qr_title: "Your QR for any amount",
  breb_key_static_qr_body:
    "Print it or share it: whoever scans it types the amount in their banking app and the money reaches your COP wallet.",
  breb_key_static_qr_generating: "We are generating your QR. It will show up here in a few seconds.",
  breb_key_pending_badge: "Registering",
  breb_key_last_failure_title: "Your key was not registered",
  breb_key_last_failure_taken:
    "The key {{key}} was already in use at another institution. Choose another option.",
  breb_key_last_failure_rejected: "Bre-B did not accept the key {{key}}. Choose another option.",
  breb_key_last_failure_unfinished:
    "We could not finish registering the key {{key}}. Try again or contact support.",
  breb_key_last_failure_custom_taken:
    "The key {{key}} was already in use at another institution. Contact support if you want another custom key.",
  breb_key_last_failure_custom_rejected:
    "Bre-B did not accept the key {{key}}. Contact support if you want another custom key.",
  breb_key_last_failure_custom_unfinished:
    "We could not finish registering the key {{key}}. Contact support to review it.",
  breb_key_primary_label: "Your primary key",
  breb_key_entry_more: "+{{count}}",
  breb_key_entry_more_label: "Additional keys: {{count}}",
  breb_key_list_title: "Your keys",
  breb_key_list_hint:
    "Your collections with an amount use the primary key. You can pick another one when you create a collection.",
  breb_key_list_add: "Add another key",
  breb_key_primary_badge: "Primary",
  breb_key_source_document: "Document",
  breb_key_source_phone: "Mobile",
  breb_key_source_generated: "Assigned",
  breb_key_source_custom: "Custom",
  breb_key_set_primary: "Make primary",
  breb_key_set_primary_done: "{{key}} is now your primary key.",
  breb_key_delete_body_primary:
    "You will stop receiving transfers to {{key}} and payments through its QR for any amount. Your next collections with an amount will use {{next}}, which becomes your primary key.",
  breb_key_delete_body_secondary:
    "You will stop receiving transfers to {{key}} and payments through its QR for any amount. Your primary key stays the same.",
  breb_key_add_cancel: "Cancel",
  breb_key_add_none: "You already have every key your account details allow.",
  breb_key_options_load_error: "We could not load the key options.",
  breb_key_confirm_qr_secondary:
    "Your collections with an amount will keep using your primary key. You can change it at any time.",
  breb_key_select_label: "Key that receives the payment",
  breb_key_select_hint: "The collection's QR is generated over this key.",
  breb_key_select_option_primary: "{{source}} · Primary",
  payin_detail_receiving_key: "Receiving key",
  payin_detail_channel: "How it arrived",
  payin_detail_channel_qr: "QR code",
  payin_detail_channel_key_unnamed: "Transfer to your Bre-B key",
  payin_detail_channel_static_qr: "QR for any amount",
  payin_detail_payer: "Payer",
  payin_detail_sent: "Sent by the payer",
  payin_detail_fee: "Fee",
  payin_detail_credited: "Credited",

  breb_key_show_qr: "Show QR",
  breb_key_quick_title: "Your Bre-B keys",
  breb_key_quick_hint: "Share them to get paid from any bank, for whatever amount they send.",
  breb_key_quick_manage: "Manage",
  breb_key_qr_view_title: "Your key QR",
  breb_key_qr_view_details: "See key details",
};
